import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";

import FlightResultCard, { getDurationMinutes, getPaxTotal } from "./FlightResultCard";

const ACCENT = "#4f3df5";

const TIME_SLOTS = [
  { value: "early", label: "Before 6 AM", icon: "bi-moon-stars", from: 0, to: 6 },
  { value: "morning", label: "6 AM - 12 PM", icon: "bi-sunrise", from: 6, to: 12 },
  { value: "afternoon", label: "12 PM - 6 PM", icon: "bi-sun", from: 12, to: 18 },
  { value: "night", label: "After 6 PM", icon: "bi-sunset", from: 18, to: 24 },
];

const SORTS = [
  { value: "cheapest", label: "Cheapest", icon: "bi-currency-rupee" },
  { value: "fastest", label: "Fastest", icon: "bi-lightning-charge" },
  { value: "earliest", label: "Earliest", icon: "bi-clock" },
];

const initialRefine = { stops: "", slots: [], airlines: [], refundableOnly: false };

const hourOf = (t) => Number(String(t || "0").slice(0, 2));
const formatINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

const FlightSearchResults = ({ results, loading, searched, search }) => {
  const [sortBy, setSortBy] = useState("cheapest");
  const [refine, setRefine] = useState(initialRefine);

  // A new search starts with clean filters
  useEffect(() => {
    setRefine(initialRefine);
  }, [results]);

  const pax = search?.pax_detail;

  const airlineOptions = useMemo(() => {
    const map = {};
    results.forEach((r) => {
      const code = r.airline?.airline_code;
      if (!code) return;
      const price = getPaxTotal(r, pax);
      if (!map[code]) map[code] = { code, name: r.airline?.airline_name, count: 0, minPrice: price };
      map[code].count += 1;
      map[code].minPrice = Math.min(map[code].minPrice, price);
    });
    return Object.values(map).sort((a, b) => a.minPrice - b.minPrice);
  }, [results, pax]);

  const visible = useMemo(() => {
    const list = results.filter((r) => {
      const stops = Number(r.flight_stops || 0);
      if (refine.stops === "0" && stops != 0) return false;
      if (refine.stops === "1+" && stops == 0) return false;
      if (refine.refundableOnly && !r.is_refundable) return false;
      if (refine.airlines.length && !refine.airlines.includes(r.airline?.airline_code)) return false;
      if (refine.slots.length) {
        const h = hourOf(r.origin?.time);
        const inSlot = TIME_SLOTS.some((s) => refine.slots.includes(s.value) && h >= s.from && h < s.to);
        if (!inSlot) return false;
      }
      return true;
    });

    const sorters = {
      cheapest: (a, b) => getPaxTotal(a, pax) - getPaxTotal(b, pax),
      fastest: (a, b) => getDurationMinutes(a) - getDurationMinutes(b),
      earliest: (a, b) => String(a.origin?.time).localeCompare(String(b.origin?.time)),
    };
    return [...list].sort(sorters[sortBy]);
  }, [results, refine, sortBy, pax]);

  const toggleIn = (key, value) =>
    setRefine((prev) => ({
      ...prev,
      [key]: prev[key].includes(value) ? prev[key].filter((v) => v != value) : [...prev[key], value],
    }));

  const sortSummary = useMemo(() => {
    if (!visible.length) return {};
    const by = (fn) => [...visible].sort(fn)[0];
    const cheapest = by((a, b) => getPaxTotal(a, pax) - getPaxTotal(b, pax));
    const fastest = by((a, b) => getDurationMinutes(a) - getDurationMinutes(b));
    const earliest = by((a, b) => String(a.origin?.time).localeCompare(String(b.origin?.time)));
    return {
      cheapest: formatINR(getPaxTotal(cheapest, pax)),
      fastest: `${Math.floor(getDurationMinutes(fastest) / 60)}h ${getDurationMinutes(fastest) % 60}m`,
      earliest: String(earliest.origin?.time).slice(0, 5),
    };
  }, [visible, pax]);

  if (!searched && !loading) {
    return (
      <div className="fr-empty">
        <i className="bi bi-airplane-engines" style={{ fontSize: 40, color: ACCENT }}></i>
        <div className="fr-empty-title">Find the best fares</div>
        <div className="fr-muted">Choose your route, dates and travellers above, then press Search.</div>
      </div>
    );
  }

  const s = search?.sector;
  const travellers = (pax?.adults || 0) + (pax?.children || 0) + (pax?.infants || 0);
  const hasRefine = refine.stops || refine.slots.length || refine.airlines.length || refine.refundableOnly;

  return (
    <div className="row g-3">
      {/* Filters */}
      <div className="col-12 col-lg-3">
        <div className="fr-panel">
          <div className="d-flex justify-content-between align-items-center mb-3">
            <div className="fr-panel-title"><i className="bi bi-funnel me-2" style={{ color: ACCENT }}></i>Filters</div>
            {hasRefine ? (
              <button type="button" className="fr-link" onClick={() => setRefine(initialRefine)}>Clear all</button>
            ) : null}
          </div>

          <div className="fr-filter-title">Stops</div>
          <div className="d-flex gap-2 mb-3">
            {[{ v: "0", l: "Non Stop" }, { v: "1+", l: "1+ Stops" }].map((o) => (
              <button
                type="button"
                key={o.v}
                className={`fr-pill flex-fill ${refine.stops === o.v ? "active" : ""}`}
                onClick={() => setRefine({ ...refine, stops: refine.stops === o.v ? "" : o.v })}
              >
                {o.l}
              </button>
            ))}
          </div>

          <div className="fr-filter-title">Departure Time</div>
          <div className="row g-2 mb-3">
            {TIME_SLOTS.map((slot) => (
              <div className="col-6" key={slot.value}>
                <button
                  type="button"
                  className={`fr-slot ${refine.slots.includes(slot.value) ? "active" : ""}`}
                  onClick={() => toggleIn("slots", slot.value)}
                >
                  <i className={`bi ${slot.icon}`}></i>
                  <span>{slot.label}</span>
                </button>
              </div>
            ))}
          </div>

          <label className="d-flex align-items-center gap-2 mb-3" style={{ cursor: "pointer", fontSize: 13 }}>
            <input
              type="checkbox"
              className="form-check-input m-0"
              checked={refine.refundableOnly}
              onChange={(e) => setRefine({ ...refine, refundableOnly: e.target.checked })}
            />
            Refundable fares only
          </label>

          {airlineOptions.length > 0 && (
            <>
              <div className="fr-filter-title">Airlines</div>
              {airlineOptions.map((a) => (
                <label key={a.code} className="d-flex align-items-center justify-content-between gap-2 py-1" style={{ cursor: "pointer", fontSize: 13 }}>
                  <span className="d-flex align-items-center gap-2 min-w-0">
                    <input
                      type="checkbox"
                      className="form-check-input m-0"
                      checked={refine.airlines.includes(a.code)}
                      onChange={() => toggleIn("airlines", a.code)}
                    />
                    <span className="text-truncate">{a.name} ({a.count})</span>
                  </span>
                  <span className="fr-muted text-nowrap">{formatINR(a.minPrice)}</span>
                </label>
              ))}
            </>
          )}
        </div>
      </div>

      {/* Results */}
      <div className="col-12 col-lg-9">
        {s && (
          <div className="d-flex flex-wrap justify-content-between align-items-end gap-2 mb-3">
            <div>
              <div className="fr-heading">
                Flights from {s.origin} to {s.destination}
              </div>
              <div className="fr-muted">
                {moment(s.departure_date).format("ddd, D MMM YYYY")}
                {s.return_date ? ` - ${moment(s.return_date).format("ddd, D MMM YYYY")}` : ""}
                {" "}&middot; {travellers} Traveller{travellers > 1 ? "s" : ""} &middot; {s.cabin_class}
              </div>
            </div>
            {!loading && (
              <div className="fr-muted">
                Showing <b>{visible.length}</b> of <b>{results.length}</b> flight{results.length == 1 ? "" : "s"}
              </div>
            )}
          </div>
        )}

        <div className="fr-sortbar">
          {SORTS.map((o) => (
            <button
              type="button"
              key={o.value}
              className={`fr-sort ${sortBy == o.value ? "active" : ""}`}
              onClick={() => setSortBy(o.value)}
            >
              <span className="d-flex align-items-center gap-2">
                <i className={`bi ${o.icon}`}></i> {o.label}
              </span>
              <span className="fr-sort-sub">{sortSummary[o.value] || "-"}</span>
            </button>
          ))}
        </div>

        {loading ? (
          [1, 2, 3].map((i) => <div key={i} className="fr-card fr-skeleton" />)
        ) : visible.length == 0 ? (
          <div className="fr-empty">
            <i className="bi bi-emoji-frown" style={{ fontSize: 36, color: "#b0b8c9" }}></i>
            <div className="fr-empty-title">No flights found</div>
            <div className="fr-muted">
              {results.length ? "No flights match the selected filters." : "Try another date or route."}
            </div>
            {hasRefine ? (
              <button type="button" className="fr-link mt-2" onClick={() => setRefine(initialRefine)}>Clear filters</button>
            ) : null}
          </div>
        ) : (
          visible.map((fare) => <FlightResultCard key={fare.fare_reference_code} fare={fare} pax={pax} />)
        )}
      </div>
    </div>
  );
};

export default FlightSearchResults;
